import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { SkillsDiagram } from "@/components/save/SkillsDiagram";
import { usePendingScope } from "@/lib/pending-edits";
import { toStoragePercent } from "@/lib/skill-levels";
import type { FieldDef } from "@/lib/field-util";
import { LocalizedError } from "@/lib/localized-error";
import { COMPANY_SCOPE_COMPETENCY } from "./pending-scopes";
import { useCompanyCompetencyDraft } from "./use-company-competency-draft";
import {
  MAX_COMPETENCY_COUNT,
  MAX_COMPETENCY_LABEL_LENGTH,
  type UseCompanyAdminResult,
} from "./use-company-admin";

// The company's expertise — the match-relevant core (company-profiles/0004-company-capabilities
// §3). "Reaches the solver" is what that used to mean and no longer does: company matching is
// deferred (concept v3) and `CompanyCapabilityProjection` is deleted rather than parked (v4), so
// today this is what the company page shows and what company SEARCH indexes (concept 0005 §2).
//
// EXPERTISE IS PER-COMPANY FREE TEXT (@external-adr GENESIS-0031-company-expertise-is-per-company-free-text). There is no platform-wide vocabulary any
// more: no `competency_field` table, no picker over it, no "not in the list?" box, and no
// reference-data screen behind them. A company admin names an expertise and gives it a level; the
// label belongs to this company and the same word at another company is a different row that means
// nothing in common.
//
// THE SCREEN IS THE PERSON-PROFILE LADDER, not a form (concept v13, company-profiles/0004 §3). Three
// verbs are on it — "+" on a rung adds, a chip's panel renames or deletes it, dragging a chip
// re-levels — while the genesis INTERFACE behind them still has only two, add and remove (genesis
// @adr SPARK-0031-adrs-decide-ui-conventions-records@1 §4). Every rename and every re-level is
// therefore sent as a remove and an add, which `use-company-competency-draft` does and nothing above
// it needs to know.
//
// WHAT WENT WITH THE CATALOGUE, AND WHY IT IS NOT A REGRESSION TO WATCH FOR. Concept v3 §3 wanted
// ONE vocabulary so company vectors stayed comparable, and this screen used to carry the duplicate
// damping that made a shared list survivable — near-match suggestions, "use the existing field",
// a create that genesis could refuse as a clash. All of it is deleted. Ten companies can now each
// write "Regulatory Affairs", "Regulatory affairs" and "RegAffairs"; nothing merges them and
// nothing suggests. That was decided with the cost spelled out and is recorded in @external-adr GENESIS-0031-company-expertise-is-per-company-free-text decision's
// Consequences, so it is not a bug report waiting to be filed against this file.
//
// TWO RULES THIS FILE STILL EXISTS TO KEEP.
//
// 1. REMOVING IS NOT SETTING THE DEGREE TO 0. An absent row means NOT LISTED; a stored 0 means
//    listed and assessed as none. genesis keeps the two apart as a rule over the stored value.
//    The CANDIDATE PROJECTION this used to cite (@external-adr GENESIS-0021-company-competency-degree-stored-as-percent §5) is not the reason any more: concept v4
//    deleted the company vector space, so a company's expertise is projected nowhere and cannot rank
//    as "actively incapable" — see @adr SPARK-0003-match-case-strategy@1 decision's v4 note, "es gibt ab v4 überhaupt keinen
//    Firmen-Vektorraum, auch keinen eigenen". The rule stands on the simpler ground that a claim
//    nobody made must not be recorded as a measurement. So removal is its own explicit action calling
//    removeCompanyCompetency, and no path here ever writes a 0 to mean "gone".
//
// 2. THERE IS ONE SCALE. The degree is stored as a 0..100 percent and the seven levels are a lens
//    over it (@external-adr GENESIS-0021-company-competency-degree-stored-as-percent §1-2), so every value written here comes from the person-side
//    toStoragePercent/levelFromPercent in lib/skill-levels. No second scale, no nine steps, no
//    company-only names.
//
// A LISTED ROW DOES HAVE A LEVEL CONTROL NOW, and the rule that once forbade it is untouched.
// `setCompanyCompetency` still ADDS — a label the company already lists is refused with
// GENESIS001247 rather than re-scored — so changing a degree is still Remove then Add. What changed
// is where that lives: it is one STAGED intent here (`stageEdit`) and two requests in the draft's
// apply(), so dragging a chip to another rung is an affordance that works rather than one whose
// every click failed. The admin expresses an intention; the draft knows what genesis needs.
//
// THE DUPLICATE IS genesis's TO REFUSE, NOT THIS FILE'S TO PREVENT. Add stays live for a label
// already listed, differing in case or not, and the refusal comes back as a catalogued code
// (GENESIS001247) that use-company-admin turns into a sentence. One authority: a local copy of the
// rule would have to fold case and whitespace exactly as PostgreSQL's `lower()` does over whatever
// the company already holds, and being nearly right would hide the real answer behind a guess.
//
// HOW MANY ENTRIES IS CAPPED, AND THAT IS NOT A SECOND OPINION ABOUT FREE TEXT. genesis folds every
// label a company lists into that company's ONE global_search tsvector, and PostgreSQL refuses a
// tsvector past 1 MB — so an unbounded list lets a company reach a state where it cannot be
// reindexed, which then aborts a PLATFORM admin's industry rename for every other company in that
// industry. genesis caps it at MAX_COMPETENCY_COUNT and answers GENESIS001249; this file holds a copy
// so the refusal is given HERE rather than by the server. Note the difference from
// CompanyMemberEditor, which disables the contact-person control: the "+" on a rung stays live and
// the panel still opens, and the cap is answered when the panel is confirmed (`confirmPanelAdd`) —
// a ladder has a "+" on every rung, and greying out seven of them says less than one sentence does.
// The note is neutral text, not a warning: it is the rule, not a failure.
//
// NOTHING HERE WRITES ON THE CLICK ANY MORE. Adding and removing are STAGED — they change the list
// on screen and the SaveBar's entry, and they reach genesis when Save is pressed
// (use-company-competency-draft). Until then the whole edit view behaves as one preview, which is
// what Kevin asked for: curate the list, then commit it, rather than a stream of writes each of
// which is already applied.
//
// THE INLINE TWO-STEP ON REMOVE IS GONE WITH IT, and that is the point rather than an oversight.
// @adr SPARK-0011-destructive-actions-require-confirmation@1 asks for a confirmation before something destructive; a staged removal destroys nothing —
// it is visible on the row, reversible with Undo beside it, and dropped entirely by leaving edit
// mode. The confirmation that remains is Save.
//
// THE LADDER IS THE EDITOR, and it is the same one a person's skills use (@adr SPARK-0039-contact-persons-come-before-the-roster@1 postscript).
// There is no separate "Add an expertise" form under the list any more: the "+" on a rung adds AT
// that level, dragging a chip moves it, and clicking one opens rename and remove. Two
// representations of one list — a flat editor above, a ladder below — is exactly the drift this
// page has been spending its day undoing.
//
// ONE CONSEQUENCE, INHERITED DELIBERATELY. The diagram's inline editor holds a half-typed new entry
// in its own transient state, not in the draft, so it does not reach the SaveBar and is lost by
// navigating away. The old form's label and level DID report dirty. This is the person page's
// behaviour, unchanged, and matching it is the point; a company-only exception would be a second
// rule to keep.
interface CompetencyRow {
  label: string;
  degree: number;
}

interface CompanyCompetencyEditorProps {
  competencies: readonly CompetencyRow[];
  admin: UseCompanyAdminResult;
  /**
   * True on the create state of the page (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@1). The draft works exactly as it does on a stored
   * company — it stages and `apply()` writes — but it must not announce itself as unsaved work,
   * because the create state has its own single commit and no SaveBar.
   */
  creating?: boolean;
}

export function CompanyCompetencyEditor({
  competencies,
  admin,
  creating = false,
}: CompanyCompetencyEditorProps) {
  const intl = useIntl();
  const [message, setMessage] = useState<string | null>(null);
  const draft = useCompanyCompetencyDraft(competencies, admin);

  const listFull = draft.listFull;

  // What the ladder draws: the list as it will be once saved, minus what is on its way out. A row
  // staged for removal is not shown ON a rung — it would be indistinguishable from a live one — so
  // it is named underneath instead, where the Undo that takes it back also lives.
  const live = draft.projected.filter((entry) => !entry.isRemoved);
  const staged = draft.projected.filter((entry) => entry.isRemoved || entry.isNew || entry.isEdited);

  const label = intl.formatMessage({
    id: "company.admin.competencies.label",
    defaultMessage: "Expertise",
  });

  // ONE FIELD. The level is not among them on purpose: it comes from the rung the "+" was pressed on
  // or the rung a chip was dropped on, so asking for it again in the panel would be a second way to
  // say what the gesture already said.
  const editFields: readonly FieldDef[] = [
    {
      key: "description",
      id: "company-competency-label",
      label,
      type: "text",
      required: true,
      placeholder: intl.formatMessage({
        id: "company.admin.competencies.labelPlaceholder",
        defaultMessage: "e.g. Regulatory Affairs",
      }),
    },
  ];

  /** genesis's duplicate refusal (GENESIS001247), in this page's words. */
  function duplicateMessage(label: string): string {
    return intl.formatMessage(
      {
        id: "company.admin.competencies.duplicate",
        defaultMessage: "This company already lists the expertise ‘{label}’.",
      },
      { label },
    );
  }

  /** genesis's cap refusal (GENESIS001249), stated as the rule rather than as a failure. */
  function listFullMessage(): string {
    return intl.formatMessage(
      {
        id: "company.admin.competencies.listFull",
        defaultMessage:
          "This company already lists {max, number} expertise entries, which is the maximum. Remove one to add another.",
      },
      { max: MAX_COMPETENCY_COUNT },
    );
  }

  /**
   * Refuses what genesis would refuse, before the round trip, over the STAGED state rather than over
   * one form's contents — because there is no form any more and several entries can be wrong at
   * once. Each rule is genesis's, held here only so the answer arrives before the request.
   */
  function refusal(): string | null {
    const blank = live.find((entry) => entry.label.trim() === "");
    if (blank) {
      return intl.formatMessage({
        id: "company.admin.competencies.blank",
        defaultMessage: "An expertise needs a name. Give it one, or remove it.",
      });
    }
    const tooLong = live.find((entry) => entry.label.trim().length > MAX_COMPETENCY_LABEL_LENGTH);
    if (tooLong) {
      return intl.formatMessage(
        {
          id: "company.admin.competencies.tooLong",
          defaultMessage: "An expertise can be at most {max, number} characters long.",
        },
        { max: MAX_COMPETENCY_LABEL_LENGTH },
      );
    }
    const seen = new Set<string>();
    const duplicate = live.find((entry) => {
      const folded = entry.label.trim().toLowerCase();
      if (seen.has(folded)) return true;
      seen.add(folded);
      return false;
    });
    if (duplicate) return duplicateMessage(duplicate.label.trim());
    if (live.length > MAX_COMPETENCY_COUNT) return listFullMessage();
    return null;
  }

  async function saveOrThrow(): Promise<void> {
    const refused = refusal();
    if (refused) {
      setMessage(refused);
      throw new LocalizedError(refused);
    }
    const failures = await draft.apply();
    setMessage(failures[0] ?? null);
    if (failures.length > 0) throw new LocalizedError(failures[0]!);
  }

  function discardDraft() {
    setMessage(null);
    draft.discard();
  }

  usePendingScope(COMPANY_SCOPE_COMPETENCY, {
    // Silent while the company is still hypothetical — see CompanyEditingScopes.
    dirty: !creating && draft.dirty,
    save: saveOrThrow,
    discard: discardDraft,
  });

  return (
    <div className="flex flex-col gap-4">
      <SkillsDiagram
        entries={live.map((entry) => ({
          id: entry.original,
          description: entry.label,
          level: String(entry.degree),
          position: 0,
        }))}
        editable
        // A staged addition is a draft, and the diagram says so by labelling its panel's confirm
        // button "Add" rather than "Done". Passed explicitly because its fallback is to read the id
        // for a "draft-" prefix, and a company may legitimately name an expertise exactly that.
        draftIds={new Set(draft.projected.filter((entry) => entry.isNew).map((e) => e.original))}
        editFields={editFields}
        // Dropped on a rung: the level moves, the label does not.
        onMove={(id, level) => draft.stageEdit(id, { degree: toStoragePercent(level) })}
        // Typed in the panel: fires per keystroke, which is why stageEdit drops an intent that ends
        // up equal to what is stored rather than queueing a remove-and-re-add of an unchanged row.
        onPatch={(id, patch) => draft.stageEdit(id, { label: panelLabel(patch) })}
        onAdd={(payload) =>
          confirmPanelAdd(payload, {
            taken: draft.hasLabel,
            full: listFull,
            say: setMessage,
            duplicate: duplicateMessage,
            listFullText: listFullMessage,
            stage: draft.stageAdd,
          })
        }
        onDelete={(id) => draft.stageRemove(id)}
        onCancel={(id) => draft.unstage(id)}
        unratedLabel={intl.formatMessage({
          id: "company.page.competencies.noCapability",
          defaultMessage: "No capability",
        })}
      />

      {/* WHAT SAVE WILL DO, and the only route back for a removal — a chip that is gone from the
          ladder cannot carry its own Undo. Neutral and textual, like every other staged state on
          this page: it is a statement about what happens next, not a warning. */}
      {staged.length > 0 ? (
        <ul className="flex flex-col gap-1 border-t border-ink/5 pt-3">
          {staged.map((entry) => (
            <li key={entry.original} className="flex flex-wrap items-baseline gap-2">
              <span className="field-meta text-ink/55">
                {entry.isRemoved ? (
                  <FormattedMessage
                    id="company.admin.competencies.pendingRemoveNamed"
                    defaultMessage="‘{label}’ will be removed when you save."
                    values={{ label: entry.original }}
                  />
                ) : entry.isNew ? (
                  <FormattedMessage
                    id="company.admin.competencies.pendingAddNamed"
                    defaultMessage="‘{label}’ will be added when you save."
                    values={{ label: entry.label }}
                  />
                ) : (
                  <FormattedMessage
                    id="company.admin.competencies.pendingEditNamed"
                    defaultMessage="‘{label}’ will be rewritten when you save."
                    values={{ label: entry.original }}
                  />
                )}
              </span>
              <Button variant="secondary" size="sm" onClick={() => draft.unstage(entry.original)}>
                <FormattedMessage id="common.undo" defaultMessage="Undo" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Stated once, above nothing in particular, rather than per row: the cap is a fact about the
          whole list. Neutral text — it is the rule, not a failure. */}
      {listFull ? (
        <p className="field-meta text-ink/55">
          <FormattedMessage
            id="company.admin.competencies.listFull"
            defaultMessage="This company already lists {max, number} expertise entries, which is the maximum. Remove one to add another."
            values={{ max: MAX_COMPETENCY_COUNT }}
          />
        </p>
      ) : null}

      {message ? <p className="field-meta text-ink/80">{message}</p> : null}
    </div>
  );
}

/**
 * Reads the expertise NAME out of a panel payload.
 *
 * <p>`SkillsDiagram` types both of its callbacks as `Record<string, unknown>` — it is the shared
 * person-profile ladder and knows nothing about what a company calls its fields — so every value
 * this editor needs crosses an untyped boundary. Reading it through a named function instead of an
 * inline cast is what makes the boundary testable: `String(undefined)` is the string `"undefined"`,
 * which would reach genesis as an expertise literally named that.
 */
export function panelLabel(payload: Record<string, unknown>): string {
  return String((payload as { description?: string }).description ?? "");
}

/**
 * Reads the LEVEL out of a panel payload, as the stored 0..100 percent the ladder works in.
 *
 * <p>Zero rather than NaN when the key is absent: zero is the ladder's own "unrated" row, so a
 * payload that lost its level lands somewhere real instead of writing NaN into the draft.
 */
export function panelLevel(payload: Record<string, unknown>): number {
  return Number((payload as { level?: number | string }).level ?? 0);
}

/**
 * What happens when the panel confirms an add.
 *
 * <p>THE TWO REFUSALS GENESIS WOULD GIVE, GIVEN HERE FIRST. `stageAdd` answers a duplicate by doing
 * nothing — silently, which is the one thing an admin must not meet — and the cap is genesis's
 * GENESIS001249. Both are answered before anything is staged, so the admin is told the rule rather
 * than watching a click do nothing or waiting for a round trip to refuse them.
 *
 * <p>A BLANK NAME IS IGNORED, NOT REFUSED. Nothing was typed, so there is nothing to explain, and
 * the message already on screen stays: it may be the refusal that is the reason the panel is still
 * open. The panel's own confirm is disabled while its one required field is empty, so this is
 * defence at an untyped boundary rather than a reachable UI path — which is precisely why it lives
 * in a function a test can call directly instead of inside a JSX closure nothing can reach.
 */
export function confirmPanelAdd(
  payload: Record<string, unknown>,
  on: {
    taken: (label: string) => boolean;
    full: boolean;
    say: (message: string | null) => void;
    duplicate: (label: string) => string;
    listFullText: () => string;
    stage: (label: string, degree: number) => void;
  },
): void {
  const typed = panelLabel(payload).trim();
  if (!typed) return;
  if (on.taken(typed)) {
    on.say(on.duplicate(typed));
    return;
  }
  if (on.full) {
    on.say(on.listFullText());
    return;
  }
  on.say(null);
  on.stage(typed, panelLevel(payload));
}
